import { projectChatIdForPath } from '../lib/projectChatRoute'
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject, PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Link, useNavigate } from './nav'
import { useSelector } from 'react-redux'
import { ArrowLeft, BarChart3, ExternalLink, HelpCircle, LayoutGrid, Menu, MessageSquare, MoreHorizontal, PanelLeft, PanelRight, Plus, Search, Settings, Share2, Shield } from 'lucide-react'
import Spinner from './Spinner'
import SharedBadge from './SharedBadge'
import { isSharedProject } from '../lib/projectSharing'
import type { RootState } from '../store'
import { useGetProcessingSummaryQuery, useGetProjectMembersQuery, useGetProjectQuery, useGetProjectsQuery, usePrefetchProjectData } from '../services/projectDataApi'
import { store } from '../store'
import UserMenu from './UserMenu'
import ThemeToggle from './ThemeToggle'
import NotificationCenter from './NotificationCenter'
import ProjectSettings from './ProjectSettings'
import ShareDialog from './ShareDialog'
import ModalPortal from './ModalPortal'
import ChatHelpOverlay from './ChatHelpOverlay'
import ShareCtaBanner from './ShareCtaBanner'
import { DiscordIcon } from './DiscordIcon'
import { DISCORD_URL } from '../lib/contact'
import { trackSegmentEvent } from '../lib/segment'
import { settingsTabToSlug, settingsSlugToTab, type SettingsTab } from './settingsTabs'
import CreditMeter, { useCreditMeterReading } from './CreditMeter'
import ErrorBoundary from './ErrorBoundary'
import ChunkErrorBoundary from './ChunkErrorBoundary'
import { lazyWithRetry } from '../lib/lazyWithRetry'
import { isDevMock } from '../dev/isDevMock'
import { useAdminMode } from '../hooks/useAdminMode'
import { useExperimentalFlag } from '../hooks/useExperimentalFlag'
import { SIDEBAR_COLLAPSED_KEY, onSidebarExpandRequest } from '../lib/sidebarChat'
import { requestChatPrompt } from '../lib/chatPrompt'
import { HelpWidget, ChatHeader, type HelpWidgetEvent } from '@replayio-app-building/help-widget'
import { trackEvent } from '../lib/analytics'
import { HelpMessageBubble } from './HelpMessageBubble'
import { HelpWidgetContext } from './helpWidgetIntent'
import '@replayio-app-building/help-widget/styles.css'
import { isUnauthorizedError } from '../lib/unauthorizedError'
import AppUpdateBanner from './AppUpdateBanner'
import './AppShell.css'
import './UserMenu.css'

// The QA chat only mounts beside a project, so it loads as a deferred chunk fetched when first
// used instead of riding the eager entry bundle (QA bug bug-mtxbj0h6-u58d).
const ProjectChat = lazy(() => import('./ProjectChat'))

// Persisted width (px) of the chat pane in the desktop two-column chat layout.
const CHAT_WIDTH_KEY = 'loopqa-chat-width'

// The settings modal (and with it the team-settings panel, the billing/plan pickers, and the
// lucide-react icons only those tabs use) sits behind a dynamic-import boundary: the shell renders
// it only when the user opens settings from the user menu or a /settings deep link, so loading it
// eagerly made the entry bundle ship that rarely-visited UI on every first load. Defer it as a
// chunk the way the Admin routes are (QA bug bug-mu5qgtuh-9p61); the tab/slug helpers AppShell
// needs for `/settings/:slug` routing stay eager in ./settingsTabs.
const SettingsModal = lazyWithRetry(() => import('./SettingsModal'))

function MobileGuestMenu({ onOpenHelp }: { onOpenHelp: () => void }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current?.contains(target) || sheetRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const sheet = open ? (
    <div ref={sheetRef} className="user-menu__sheet">
      <button
        type="button"
        className="user-menu__backdrop"
        aria-label="Close menu"
        data-analytics-event="nav-guest-menu-close"
        onClick={() => setOpen(false)}
      />
      <div className="user-menu__dropdown" data-testid="mobile-guest-menu">
        <button
          type="button"
          className="user-menu__dropdown-item app-sidebar__help-btn"
          data-analytics-event="nav-guest-menu-help"
          onClick={() => {
            setOpen(false)
            onOpenHelp()
          }}
        >
          <HelpCircle size={16} aria-hidden />
          Help
        </button>
        <a
          className="user-menu__dropdown-item"
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="sidebar-discord-link"
          data-analytics-event="nav-discord-link"
          onClick={() => {
            trackSegmentEvent('Clicked Join Discord', { location: 'mobile-menu' })
            setOpen(false)
          }}
        >
          <DiscordIcon size={16} />
          Discord
        </a>
        <ThemeToggle className="user-menu__dropdown-item" />
      </div>
    </div>
  ) : null

  return (
    <div ref={containerRef}>
      <button
        type="button"
        className="app-sidebar__guest-menu-btn"
        aria-label="More options"
        aria-expanded={open}
        data-testid="mobile-guest-menu-trigger"
        data-analytics-event="nav-guest-menu-toggle"
        onClick={() => setOpen(!open)}
      >
        <Menu size={20} strokeWidth={1.75} aria-hidden />
      </button>
      {sheet && createPortal(sheet, document.body)}
    </div>
  )
}

function SidebarProjectList({
  projectSearchInputRef,
}: {
  projectSearchInputRef: RefObject<HTMLInputElement | null>
}) {
  const { user } = useSelector((state: RootState) => state.auth)
  const projectsQuery = useGetProjectsQuery({ pageSize: 50 }, { skip: !user })
  const processingQuery = useGetProcessingSummaryQuery(undefined, { skip: !user, pollingInterval: 10_000, skipPollingIfUnfocused: true })
  const items = projectsQuery.data?.items ?? []
  const loading = projectsQuery.isLoading
  const activeProjects = useMemo(
    () => new Set((processingQuery.data?.running ?? []).map((task) => task.project_id)),
    [processingQuery.data?.running],
  )
  const loc = useLocation()
  const [nameFilter, setNameFilter] = useState('')
  const prefetchProject = usePrefetchProjectData()

  const pathSegment = loc.pathname.match(/^\/projects\/([^/]+)/)?.[1]
  const activeProjectId = pathSegment && pathSegment !== 'new' ? pathSegment : undefined

  const trimmedFilter = nameFilter.trim()
  const filteredItems = useMemo(() => {
    if (!trimmedFilter) return items
    const q = trimmedFilter.toLowerCase()
    return items.filter((p) => p.name.toLowerCase().includes(q))
  }, [items, trimmedFilter])

  if (!user || (items.length === 0 && !loading)) return null

  const showSkeleton = loading && items.length === 0

  const showSidebarFilterEmpty = trimmedFilter.length > 0 && filteredItems.length === 0 && items.length > 0

  if (showSkeleton) {
    return (
      <div className="sidebar-projects">
        <div className="sidebar-projects__search">
          <div className="sidebar-projects__search-field">
            <Search size={14} strokeWidth={1.75} className="sidebar-projects__search-icon" aria-hidden />
            <input
              type="search"
              className="sidebar-projects__search-input"
              placeholder="Filter by name…"
              disabled
              aria-label="Filter projects by name"
              data-testid="sidebar-projects-search-input"
              data-analytics-event="nav-sidebar-project-search-input"
            />
          </div>
        </div>
        <div className="sidebar-projects__scroll" data-testid="sidebar-projects-loading" role="status" aria-label="Loading projects">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="sidebar-projects__skeleton-item" aria-hidden="true">
              <span className="sidebar-projects__skeleton-bar" />
              <span className="sidebar-projects__skeleton-badge" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="sidebar-projects">
      <div className="sidebar-projects__search">
        <div className="sidebar-projects__search-field">
          <Search size={14} strokeWidth={1.75} className="sidebar-projects__search-icon" aria-hidden />
          <input
            ref={projectSearchInputRef}
            type="search"
            className="sidebar-projects__search-input"
            placeholder="Filter by name…"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            aria-label="Filter projects by name"
            autoComplete="off"
            spellCheck={false}
            data-testid="sidebar-projects-search-input"
            data-analytics-event="nav-sidebar-project-search-input"
          />
        </div>
      </div>
      <div className="sidebar-projects__scroll">
        {showSidebarFilterEmpty ? (
          <p className="sidebar-projects__filter-empty" role="status">
            No matches
          </p>
        ) : (
          filteredItems.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}/overview`}
              onPointerEnter={() => prefetchProject(p.id)}
              onFocus={() => prefetchProject(p.id)}
              className={`sidebar-projects__item ${activeProjectId === p.id ? 'sidebar-projects__item--active' : ''}`}
              data-analytics-event="nav-sidebar-project-link"
            >
              <span className="sidebar-projects__name">{p.name}</span>
              {isSharedProject(p) && <SharedBadge variant="icon" className="sidebar-projects__shared" />}
              {activeProjects.has(p.id) && (
                <Spinner
                  size={14}
                  strokeWidth={2}
                  className="sidebar-projects__spinner"
                  label="Work in progress"
                  data-testid={`sidebar-project-spinner-${p.id}`}
                />
              )}
            </Link>
          ))
        )}
      </div>
    </div>
  )
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const loc = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useSelector((state: RootState) => state.auth.user)
  const projectListQuery = useGetProjectsQuery({ pageSize: 50 }, { skip: !user })
  const projectListItems = projectListQuery.data?.items ?? []
  // Viewer's role on the open project — gates whether the rail's Share dialog is editable (owners /
  // admins can toggle public sharing; everyone else sees the read-only note). Mirrors ProjectOverview.
  const chatProjectId = projectChatIdForPath(loc.pathname)
  const chatProjectQuery = useGetProjectQuery(chatProjectId!, { skip: !chatProjectId })
  const chatProject = chatProjectQuery.data ?? null
  // Do not mount the project chat until the project-level read check succeeds. Every request made by
  // ProjectChat is independently authorized by the backend, but mounting it beside a private-project
  // 401 briefly rendered its local shell and derived run entries while the page initiated sign-in.
  const canRenderProjectChat = !!chatProject && !isUnauthorizedError(chatProjectQuery.error)
  const projectMembersQuery = useGetProjectMembersQuery(chatProjectId!, { skip: !chatProjectId })
  const projectUserRole = projectMembersQuery.data?.role
  const canEditProject = projectUserRole === 'owner' || projectUserRole === 'admin'
  // The project's app URL shown in the top bar (re-added: #298 restored the render but not this const).
  const chatProjectUrl = chatProject?.target_url || chatProject?.github?.repoUrl || null

  // The Insights nav entry (and the page behind it) is behind an experimental flag, so it only
  // appears for users who turned it on in Settings > Experimental. `enabled` stays false until the
  // user-settings request resolves, so the sidebar nav waits for `insightsLoaded` before painting:
  // letting the flagged link insert into an already-painted nav once the settings response landed
  // shoved the Projects section down by the link's height plus the nav gap (bug-mu8zaw1l-zk8z).
  const { enabled: insightsEnabled, loaded: insightsLoaded } = useExperimentalFlag('insights')

  const dashboardActive = loc.pathname === '/'
  const projectsNavActive =
    loc.pathname === '/projects' ||
    loc.pathname.startsWith('/projects/')
  // The project id when viewing a project surface that hosts the sidebar chat. As well as the overview
  // itself, this covers the per-item detail routes (bug / journey) so that
  // clicking a test-run entry's exploration, journey, or bug in the chat opens it in the main pane on
  // the right while the chat sidebar stays put — rather than collapsing back to the project list.
  // Task-detail pages are intentionally EXCLUDED: they are admin-only full-detail pages that should
  // render at full width with neither the side chat column nor the chat top bar, so they fall through
  // to the standard layout instead of the chat-project layout.
  // The top bar's project title must stay put when the user drills from the overview into a bug or
  // journey detail page — those detail routes live under this same chat layout. Reading only
  // `chatProject` alone made the title fall back to a generic "Project"
  // there: ProjectOverview clears its local detail on unmount, BugDetail only repopulates it when the
  // project isn't already in the sidebar list, and JourneyDetail never populates it at all. Resolve
  // the name from whichever source knows this project — the loaded detail (when it matches the open
  // project) or the sidebar's projects list — so it persists across the drill-in.
  const chatProjectName = useMemo(() => {
    if (!chatProjectId) return undefined
    if (chatProject?.id === chatProjectId) return chatProject.name
    return projectListItems.find((p) => p.id === chatProjectId)?.name
  }, [chatProjectId, chatProject, projectListItems])
  // Whether the name above is merely late or is never coming. Both sources having settled without it
  // means the id is gone or inaccessible (a deleted project, a link the viewer can't open) — the page
  // shows its own error, and the title has to stop waiting or it pulses forever.
  const chatProjectNamePending =
    !!chatProjectId && (chatProjectQuery.isLoading || projectListQuery.isLoading)
  // The Share / Project Settings modals must open on every project surface, including the drill-down
  // detail pages (bug / journey) that live under this same chat layout. Gating them on `chatProject`
  // alone left the top-bar buttons doing nothing there: those pages
  // don't reliably populate the loaded detail (ProjectOverview clears it on unmount; BugDetail
  // resolves its project from the sidebar list and only fetches the detail when it's absent). Resolve
  // the project the same way `chatProjectName` and BugDetail do — prefer the loaded detail when it
  // matches the open project, else fall back to the sidebar list item — so the modals have something
  // to render regardless of navigation depth.
  const chatProjectForModal = useMemo(() => {
    if (!chatProjectId) return null
    if (chatProject?.id === chatProjectId) return chatProject
    return projectListItems.find((p) => p.id === chatProjectId) ?? null
  }, [chatProjectId, chatProject, projectListItems])
  // A single bug report is open (…/bugs/:bugId, not the …/bugs list). On mobile the chat/project
  // split collapses to just the bug report — no Chat/Project selector — so a bug report reads as a
  // full-screen page (matching the standard layout) rather than one pane of a toggle.
  const onBugDetail = /^\/projects\/[^/]+\/bugs\/[^/]+/.test(loc.pathname)

  const [collapsed, setCollapsed] = useState(false)
  const [isMobileLayout, setIsMobileLayout] = useState(false)
  const projectSearchInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      if (v === '1') setCollapsed(true)
    } catch {
      /* ignore */
    }
  }, [])

  // Expand the sidebar on request (e.g. a project was just created from the onboarding chat) so the
  // chat is visible in the sidebar rather than tucked behind the collapsed rail. The preference is
  // already persisted by the requester; here we react in the currently-mounted shell.
  useEffect(() => onSidebarExpandRequest(() => setCollapsed(false)), [])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const sync = () => setIsMobileLayout(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const effectiveCollapsed = collapsed && !isMobileLayout
  // Chat-shell sidebar (desktop): the sidebar uses one layout — a user row + icon actions at the top,
  // then a lower panel. Only the panel's content changes with the route: the project's QA chat when a
  // project overview is open, or the project list otherwise. Using the same rendering path in both
  // states keeps the sidebar from restructuring as you open/close a project. Mobile falls back to the
  // normal nav layout.
  const chatShell = !isMobileLayout
  // When a project is open (desktop), use a dedicated full-page layout modelled
  // on nut.new: a top bar (back button + project title on the left, Help / Discord / user on the
  // right) over a two-column body — the QA chat on the left, the project view on the right — instead
  // of the standard sidebar + main. The chat sidebar layout below still handles the no-project states.
  // Credits belong to the team that funds the project, not to whoever is looking at it: a teammate
  // spends the owner's balance. `team_id` is null on projects that predate teams, which bill to their
  // owner's own account — the null the meter's hook already falls back to.
  const fundingTeamId = chatProject?.team_id ?? null
  const creditReading = useCreditMeterReading(chatProjectId ? fundingTeamId : null)

  const chatProjectLayout = chatShell && !!chatProjectId
  // Mobile equivalent: the desktop two-column layout doesn't fit a phone, so with a project open we
  // show one pane at a time (chat or project view) and a bottom selector to switch between them —
  // modelled on nut.new's mobile chat/workbench toggle.
  const mobileChatProject = isMobileLayout && !!chatProjectId
  // Either full-page chat layout owns the main content, so the fallback <main> below must stand down.
  const chatFullLayout = chatProjectLayout || mobileChatProject
  // Default a freshly-opened project to its Project view (not the chat): the project overview is the
  // page users expect to land on, and the chat is one tap away via the selector.
  const [mobileChatView, setMobileChatView] = useState<'chat' | 'project'>('project')
  // On a bug report the split is suppressed and the project pane always wins, regardless of the last
  // selected view, so navigating overview → bug never lands on a stale chat pane.
  const effectiveMobileView = onBugDetail ? 'project' : mobileChatView

  // Mobile new-message cue: when the project pane is showing and a new agent message lands in the
  // chat, flag the Chat tab (an animated dot) so the user knows to switch back. Cleared the moment the
  // chat pane becomes visible. Refs mirror the current view / layout so the handler passed to
  // ProjectChat stays referentially stable yet reads the latest values.
  const [chatHasUnseen, setChatHasUnseen] = useState(false)
  const mobileChatProjectRef = useRef(mobileChatProject)
  mobileChatProjectRef.current = mobileChatProject
  const effectiveMobileViewRef = useRef(effectiveMobileView)
  effectiveMobileViewRef.current = effectiveMobileView
  const handleAssistantMessage = useCallback(() => {
    if (mobileChatProjectRef.current && effectiveMobileViewRef.current !== 'chat') setChatHasUnseen(true)
  }, [])
  useEffect(() => {
    if (effectiveMobileView === 'chat') setChatHasUnseen(false)
  }, [effectiveMobileView])

  // Desktop in-project chrome (nut.new-style): the far-left icon rail switches the left panel between
  // the QA chat, the project settings, and the share UI — each takes over the left pane (like builder
  // swapping chat ↔ settings) rather than opening a modal, to keep the interaction consistent. Reset
  // to the chat panel whenever the open project changes so a stale settings/share view never lingers.
  // In-project Share / Settings chrome. Desktop: buttons in the top bar open these as modals over the
  // project page. Mobile: a top-bar "…" menu holds Help / Discord / Share / Settings, and Share/Settings
  // open as a full-screen takeover (like builder's account page). Reset all when the open project changes.
  const [projectModal, setProjectModal] = useState<'share' | 'settings' | null>(null)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [mobileProjectPanel, setMobileProjectPanel] = useState<'share' | 'settings' | null>(null)
  useEffect(() => {
    setProjectModal(null)
    setMoreMenuOpen(false)
    setMobileProjectPanel(null)
  }, [chatProjectId])
  // Legacy links open the existing project overlays, then clear the one-shot request.
  useEffect(() => {
    if (!chatProjectId) return
    const panel = searchParams.get('project-settings') === '1' ? 'settings'
      : searchParams.get('share') === '1' ? 'share' : null
    if (!panel) return
    setProjectModal(panel)
    setMobileProjectPanel(panel)
    const next = new URLSearchParams(searchParams)
    next.delete(panel === 'settings' ? 'project-settings' : 'share')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, chatProjectId])
  // Escape closes the desktop Share/Settings modal (ShareDialog also handles its own, harmlessly).
  useEffect(() => {
    if (!projectModal) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setProjectModal(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [projectModal])

  // Adjustable width of the chat pane in the desktop two-column layout. Null until the user drags the
  // divider (or a saved width is restored), when the CSS default (clamp) applies. Persisted so the
  // choice survives reloads.
  const [chatWidth, setChatWidth] = useState<number | null>(null)
  const chatPaneRef = useRef<HTMLElement>(null)
  useEffect(() => {
    try {
      const v = localStorage.getItem(CHAT_WIDTH_KEY)
      if (v) {
        const n = parseInt(v, 10)
        if (!Number.isNaN(n)) setChatWidth(n)
      }
    } catch {
      /* ignore */
    }
  }, [])

  const onChatResizeStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = chatPaneRef.current?.getBoundingClientRect().width ?? 360
    const onMove = (ev: PointerEvent) => {
      const max = Math.min(window.innerWidth - 360, 760)
      const w = Math.max(300, Math.min(startW + (ev.clientX - startX), Math.max(320, max)))
      setChatWidth(w)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      setChatWidth((w) => {
        if (w != null) {
          try { localStorage.setItem(CHAT_WIDTH_KEY, String(Math.round(w))) } catch { /* ignore */ }
        }
        return w
      })
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  const helpToggleRef = useRef<(() => void) | null>(null)
  // Whether the help widget panel is currently open, mirrored from the widget's renderButton hook.
  const helpOpenRef = useRef(false)

  // Open (never toggle-closed) the help widget — for entry points like the project overview's
  // "Open chat" button that should reveal the assistant, not dismiss it if already open.
  const openGeneralHelp = useCallback(() => {
    if (helpOpenRef.current) return
    helpToggleRef.current?.()
  }, [])
  const helpController = useMemo(() => ({ openGeneralHelp }), [openGeneralHelp])

  // The Help button forks on admin. Regular users get an overlay pointing them at the in-app chat
  // (the intended way to ask questions about what the product can do) and the Discord community;
  // admins get the actual help widget panel, toggled so a second click dismisses it. All general
  // "Help" entry points route through this so the behaviour is consistent whichever button is used.
  //
  // `useAdminMode` (not `useIsAdmin`): the Help button sits on user-facing surfaces where the profile
  // menu's "Admin Features" toggle is reachable, so an admin previewing the app as a normal user
  // should see the overlay too. That's a view preference — the widget's APIs enforce real admin.
  const adminMode = useAdminMode()
  const [chatHelpOpen, setChatHelpOpen] = useState(false)
  const onHelp = useCallback(() => {
    if (adminMode) {
      helpToggleRef.current?.()
      return
    }
    setChatHelpOpen(true)
  }, [adminMode])

  // The help widget invokes this from its background poll loop whenever the assistant emits a
  // `navigate` action — i.e. with no user gesture. It must stay a client-side route change: a
  // window.location assignment here reloads the whole app, which users experience as the page
  // refreshing out of nowhere mid-conversation (and it drops the open widget panel too).
  const routerNavigate = useNavigate()
  const navigate = useCallback((path: string) => {
    if (/^[a-z][a-z0-9+.-]*:|^\/\//i.test(path) || path.startsWith('/api/')) {
      // Absolute/external URLs and non-SPA endpoints can't go through the router.
      window.location.href = path
      return
    }
    routerNavigate(path)
  }, [routerNavigate])

  // The settings modal is driven entirely by the URL so every tab is deep-linkable, bookmarkable,
  // and shareable: `/settings/<slug>` opens the modal on that tab (automated emails link here — see
  // SETTINGS_TAB_SLUGS). Opening from within the app stashes the current location as
  // `backgroundLocation` (in history state) so the page behind the modal is preserved and closing
  // returns to it; a cold deep-link (e.g. from an email) has no background and renders the projects
  // list behind the modal instead (see the `/settings/*` routes in App.tsx).
  const settingsOpen = loc.pathname === '/settings' || loc.pathname.startsWith('/settings/')
  const settingsSlug = loc.pathname.match(/^\/settings\/([^/]+)/)?.[1] ?? null
  const settingsTab = settingsSlug === 'account' && new URLSearchParams(loc.search).get('manage') === 'subscription'
    ? 'plan' : settingsSlugToTab(settingsSlug) ?? 'account'
  const settingsBackground = (loc.state as { backgroundLocation?: { pathname: string; search: string; hash: string } } | null)?.backgroundLocation ?? null

  const openSettings = useCallback((tab: SettingsTab = 'account') => {
    routerNavigate(`/settings/${settingsTabToSlug(tab)}`, {
      state: { backgroundLocation: { pathname: loc.pathname, search: loc.search, hash: loc.hash } },
    })
  }, [routerNavigate, loc.pathname, loc.search, loc.hash])

  const changeSettingsTab = useCallback((tab: SettingsTab) => {
    // Reflect the active tab in the URL without spamming history (replace), preserving the
    // background location so closing still returns to the originating page.
    routerNavigate(`/settings/${settingsTabToSlug(tab)}`, {
      replace: true,
      state: settingsBackground ? { backgroundLocation: settingsBackground } : undefined,
    })
  }, [routerNavigate, settingsBackground])

  const closeSettings = useCallback(() => {
    if (settingsBackground) {
      routerNavigate({ pathname: settingsBackground.pathname, search: settingsBackground.search, hash: settingsBackground.hash })
    } else {
      routerNavigate('/projects')
    }
  }, [routerNavigate, settingsBackground])

  // Backward-compatibility: older links (and any emails already sent) open settings via a
  // `?settings=<tab>` query param on a normal page. Redirect those into the canonical
  // `/settings/<slug>` path, keeping the current page as the background so closing returns to it.
  useEffect(() => {
    const params = new URLSearchParams(loc.search)
    const requested = params.get('settings')
    if (!requested) return
    const tab = settingsSlugToTab(requested)
    if (!tab) return
    params.delete('settings')
    routerNavigate(`/settings/${settingsTabToSlug(tab)}`, {
      replace: true,
      state: { backgroundLocation: { pathname: loc.pathname, search: params.toString() ? `?${params.toString()}` : '', hash: loc.hash } },
    })
  }, [loc.search, loc.pathname, loc.hash, routerNavigate])
  // The help widget serializes this snapshot into the POST body of
  // `new-help-widget-message-background` — a Netlify BACKGROUND function, whose invocation
  // payload the platform caps at 256KB. An oversized body is rejected with a platform 500
  // before the handler runs, the widget client swallows the error, and the user is left with
  // an infinite spinner (no conversation row is ever created). Pages like the guidance update
  // detail hold megabytes in the store (entry contents, diffs, task logs), so bound the
  // snapshot: drop any slice that serializes too large, leaving a size marker in its place.
  const getAppState = useCallback(() => {
    const SLICE_BUDGET = 8 * 1024
    const TOTAL_BUDGET = 96 * 1024
    const state = store.getState() as unknown as Record<string, unknown>
    const out: Record<string, unknown> = {}
    let total = 0
    for (const [key, slice] of Object.entries(state)) {
      let json = ''
      try {
        json = JSON.stringify(slice) ?? ''
      } catch {
        json = ''
      }
      if (!json || json.length > SLICE_BUDGET || total + json.length > TOTAL_BUDGET) {
        out[key] = `[omitted: ${json ? `${json.length} bytes` : 'unserializable'}]`
        continue
      }
      out[key] = slice
      total += json.length
    }
    return out
  }, [])

  // Report every help widget interaction (open/close, message sent, element picker, reset) as an
  // analytics event. The tracker stamps each event with the active help widget session id as
  // metadata (see src/lib/analytics.ts), so these — and all other events — deep-link to the
  // session's recording from the admin activity pages. We forward only non-sensitive context: never
  // the raw message text, just its length and any picked element's tag.
  const handleHelpEvent = useCallback((event: HelpWidgetEvent) => {
    const data: Record<string, unknown> = {}
    if (event.type === 'message_sent') {
      data.messageLength = event.message.length
      if (event.elementContext) data.elementTag = event.elementContext.elementTag
    } else if (event.type === 'element_selected') {
      data.elementTag = event.elementContext.elementTag
    }
    trackEvent(`help-widget-${event.type}`, data, `help-widget-${event.type}`)
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  // While collapsed, clicking anywhere on the rail that ISN'T an interactive item (a nav link, button,
  // input, etc.) expands the sidebar — so the whole rail acts as an expand affordance while individual
  // items keep their own click behavior (navigate / open the profile menu). The dedicated expand button
  // remains the keyboard/AT-accessible control; this is a pointer convenience layered on top.
  const handleCollapsedRailClick = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a, button, input, textarea, select, [role="button"], [role="menuitem"], [role="menu"]')) {
      return
    }
    setCollapsed(false)
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, '0')
    } catch {
      /* ignore */
    }
  }, [])

  // The user / Help / Discord cluster. Normally sits in the sidebar footer; in chat mode it is
  // relocated to the top so the chat can own the lower portion of the sidebar.
  const sidebarFooterItems = (
    <>
      {/* Signed-in desktop users get Help and Discord inside the profile menu (see UserMenu), so the
          footer only carries them for signed-out desktop users, who have no profile dropdown. On mobile
          they fold into the guest menu / profile sheet instead. */}
      {!isMobileLayout && !user && (
        <>
          {!effectiveCollapsed ? (
            <button
              type="button"
              className="app-sidebar__link app-sidebar__help-btn"
              onClick={onHelp}
              data-analytics-event="nav-help-button"
            >
              <HelpCircle size={18} strokeWidth={1.75} aria-hidden />
              Help
            </button>
          ) : (
            <div className="app-sidebar__tooltip-host">
              <button
                type="button"
                className="app-sidebar__link app-sidebar__link--icon-only app-sidebar__help-btn"
                onClick={onHelp}
                aria-label="Help"
                data-analytics-event="nav-help-button"
              >
                <HelpCircle size={18} strokeWidth={1.75} aria-hidden />
              </button>
              <span className="app-sidebar__tooltip" role="tooltip">Help</span>
            </div>
          )}
          {!effectiveCollapsed ? (
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              className="app-sidebar__link"
              data-testid="sidebar-discord-link"
              data-analytics-event="nav-discord-link"
              onClick={() => trackSegmentEvent('Clicked Join Discord', { location: 'sidebar' })}
            >
              <DiscordIcon />
              Discord
            </a>
          ) : (
            <div className="app-sidebar__tooltip-host">
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                className="app-sidebar__link app-sidebar__link--icon-only"
                aria-label="Discord"
                data-testid="sidebar-discord-link"
                data-analytics-event="nav-discord-link"
                onClick={() => trackSegmentEvent('Clicked Join Discord', { location: 'sidebar' })}
              >
                <DiscordIcon />
              </a>
              <span className="app-sidebar__tooltip" role="tooltip">Discord</span>
            </div>
          )}
        </>
      )}
      {!user && isMobileLayout && (
        <MobileGuestMenu onOpenHelp={onHelp} />
      )}
      <div className="app-sidebar__account">
        <UserMenu
          sidebarCollapsed={effectiveCollapsed}
          onOpenHelp={onHelp}
          onOpenSettings={openSettings}
        />
        {user && !effectiveCollapsed && <NotificationCenter variant="sidebar" />}
      </div>
    </>
  )

  return (
    <HelpWidgetContext.Provider value={helpController}>
    <div className="app-layout">
      <a href="#main-content" className="skip-nav" data-testid="skip-nav-link" data-analytics-event="nav-skip-nav-link">
        Skip to main content
      </a>
      {isDevMock() && (
        <div className="app-layout__mock-banner" role="status">
          Dev mock data — no database or auth backend
        </div>
      )}
      <AppUpdateBanner />
      {/* CTA bar for logged-out visitors who arrived via a project share link (renders nothing
          otherwise). Above the body so it spans the full width at the top of the viewport. */}
      <ShareCtaBanner />
      <div
        className={`app-layout__body ${effectiveCollapsed && !chatFullLayout ? 'app-layout__body--sidebar-collapsed' : ''} ${chatFullLayout ? 'app-layout__body--chat-project' : ''}`}
      >
        {chatFullLayout ? (
          <div
            className={`chat-project ${mobileChatProject ? 'chat-project--mobile' : ''} ${mobileChatProject && onBugDetail ? 'chat-project--mobile-bug' : ''}`}
            data-testid="chat-project-layout"
            data-mobile-view={mobileChatProject ? effectiveMobileView : undefined}
          >
            <div className="chat-project__frame">
              <header className="chat-topbar" data-testid="chat-topbar">
                <div
                  className="chat-topbar__chat-region"
                  style={!mobileChatProject && chatWidth != null ? { flexBasis: `${chatWidth + 8}px` } : undefined}
                >
                  <>
                    <Link
                      to="/"
                      className="chat-topbar__logo"
                      aria-label="Replay QA home"
                      title="Replay QA home"
                      data-testid="chat-topbar-logo"
                      data-analytics-event="chat-topbar-logo"
                    >
                      <img src="/images/logo.svg" alt="" width="22" height="26" />
                    </Link>
                    <button
                      type="button"
                      className="chat-topbar__back"
                      onClick={() => navigate('/projects')}
                      aria-label="Back to projects"
                      title="Back to projects"
                      data-testid="chat-topbar-back"
                      data-analytics-event="chat-topbar-back"
                    >
                      <ArrowLeft size={18} strokeWidth={1.75} aria-hidden />
                    </button>
                  </>
                  <span className="chat-topbar__sep" aria-hidden />
                  <div className="chat-topbar__identity">
                    <h1 className="chat-topbar__title" data-testid="chat-topbar-title">
                      {/* Neither source knows the name yet on a cold load, and a placeholder word would
                          visibly rewrite itself to the real name a moment later. */}
                      {chatProjectName ??
                        (chatProjectNamePending ? (
                          <>
                            <span className="sr-only">Loading project</span>
                            <span className="chat-topbar__title-skeleton" aria-hidden />
                          </>
                        ) : (
                          'Project'
                        ))}
                    </h1>
                    {chatProjectUrl && (
                      <a
                        className="chat-topbar__url"
                        href={chatProjectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={chatProjectUrl}
                        data-testid="chat-topbar-url"
                      >
                        <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
                        <span className="chat-topbar__url-text">{chatProjectUrl}</span>
                      </a>
                    )}
                  </div>
                </div>
                <div className="chat-topbar__project-region">
                  <div className="chat-topbar__right">
                    {!mobileChatProject ? (
                      <>
                      {creditReading && (
                        <CreditMeter
                          reading={creditReading}
                          variant="topbar"
                          teamId={fundingTeamId}
                          onOpenSettings={openSettings}
                        />
                      )}
                      {/* Help reveals an animated label to the left of the icon on hover/focus. The
                          label expands inline so the cluster self-heals (reflows back) once the
                          pointer leaves. See `.chat-topbar__action--labeled` in AppShell.css.
                          Discord and the Admin Features toggle now live in the profile menu. */}
                      <button
                        type="button"
                        className="chat-topbar__action chat-topbar__action--labeled"
                        onClick={onHelp}
                        aria-label="Help"
                        data-analytics-event="nav-help-button"
                      >
                        <span className="chat-topbar__action-label" aria-hidden>Help</span>
                        <HelpCircle size={18} strokeWidth={1.75} aria-hidden />
                      </button>
                      <span className="chat-topbar__sep" aria-hidden />
                      {/* Rail is gone: Share / Settings open as modals over the project page. They're
                          persistent labelled buttons (icon + label always visible) — see
                          `.chat-topbar__btn` in AppShell.css. */}
                      <button
                        type="button"
                        className="chat-topbar__btn"
                        onClick={() => setProjectModal('share')}
                        aria-label="Share project"
                        data-testid="chat-topbar-share"
                        data-analytics-event="chat-rail-share"
                      >
                        <Share2 size={16} strokeWidth={1.75} aria-hidden />
                        Share
                      </button>
                      <button
                        type="button"
                        className="chat-topbar__btn"
                        onClick={() => setProjectModal('settings')}
                        aria-label="Project settings"
                        data-testid="chat-topbar-settings"
                        data-analytics-event="chat-rail-settings"
                      >
                        <Settings size={16} strokeWidth={1.75} aria-hidden />
                        Settings
                      </button>
                      {adminMode && chatProjectId && (
                        <Link
                          to={`/admin/projects/${chatProjectId}`}
                          className="chat-topbar__btn"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open project admin"
                          data-testid="chat-topbar-admin"
                          data-analytics-event="new-overview-admin"
                        >
                          <Shield size={16} strokeWidth={1.75} aria-hidden />
                          Admin
                        </Link>
                      )}
                      {user && <NotificationCenter variant="topbar" />}
                      <UserMenu sidebarCollapsed={false} inTopBar onOpenHelp={onHelp} onOpenSettings={openSettings} />
                      </>
                    ) : (
                      <>
                      {/* Builder-style "…" menu: Share / Settings collapse here on mobile instead of
                          the desktop rail. Discord and Admin Features live in the profile menu. */}
                      <div className="chat-topbar__more">
                        <button
                          type="button"
                          className="chat-topbar__action"
                          onClick={() => setMoreMenuOpen((v) => !v)}
                          aria-label="More"
                          aria-haspopup="menu"
                          aria-expanded={moreMenuOpen}
                          data-testid="chat-topbar-more"
                          data-analytics-event="chat-topbar-more"
                        >
                          <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden />
                        </button>
                        {moreMenuOpen && (
                          <>
                            <button
                              type="button"
                              className="chat-topbar__more-backdrop"
                              aria-hidden
                              tabIndex={-1}
                              onClick={() => setMoreMenuOpen(false)}
                            />
                            <div className="chat-topbar__more-menu" role="menu" data-testid="chat-topbar-more-menu">
                              <button
                                type="button"
                                role="menuitem"
                                className="chat-topbar__more-item"
                                onClick={() => { setMoreMenuOpen(false); setMobileProjectPanel('share') }}
                                data-testid="chat-more-share"
                                data-analytics-event="chat-rail-share"
                              >
                                <Share2 size={16} strokeWidth={1.75} aria-hidden />
                                Share
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="chat-topbar__more-item"
                                onClick={() => { setMoreMenuOpen(false); setMobileProjectPanel('settings') }}
                                data-testid="chat-more-settings"
                                data-analytics-event="chat-rail-settings"
                              >
                                <Settings size={16} strokeWidth={1.75} aria-hidden />
                                Project Settings
                              </button>
                              {adminMode && chatProjectId && (
                                <Link
                                  to={`/admin/projects/${chatProjectId}`}
                                  role="menuitem"
                                  className="chat-topbar__more-item"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setMoreMenuOpen(false)}
                                  data-testid="chat-more-admin"
                                  data-analytics-event="new-overview-admin"
                                >
                                  <Shield size={16} strokeWidth={1.75} aria-hidden />
                                  Admin
                                </Link>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {creditReading && !creditReading.needsAttention && (
                        <CreditMeter
                          reading={creditReading}
                          variant="compact"
                          teamId={fundingTeamId}
                          onOpenSettings={openSettings}
                        />
                      )}
                      {user && <NotificationCenter variant="topbar" />}
                      <UserMenu sidebarCollapsed={false} inTopBar onOpenHelp={onHelp} onOpenSettings={openSettings} />
                      </>
                    )}
                  </div>
                </div>
              </header>
              {mobileChatProject && creditReading?.needsAttention && (
                <CreditMeter
                  reading={creditReading}
                  variant="banner"
                  teamId={fundingTeamId}
                  onOpenSettings={openSettings}
                />
              )}
              <div className="chat-project__body">
                <aside
                  ref={chatPaneRef}
                  className="chat-project__chat"
                  aria-label="QA chat"
                  style={!mobileChatProject && chatWidth != null ? { flexBasis: `${chatWidth}px` } : undefined}
                >
                  {canRenderProjectChat && (
                    <Suspense fallback={null}>
                      <ProjectChat
                        key={chatProjectId}
                        projectId={chatProjectId!}
                        project={chatProject}
                        onOpenProjectItem={mobileChatProject && !onBugDetail ? () => setMobileChatView('project') : undefined}
                        onAssistantMessage={handleAssistantMessage}
                      />
                    </Suspense>
                  )}
                </aside>
                {!mobileChatProject && (
                  <div
                    className="chat-project__resizer"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize chat panel"
                    onPointerDown={onChatResizeStart}
                    data-testid="chat-project-resizer"
                  />
                )}
                <main className="app-main chat-project__main" id="main-content">
                  <ErrorBoundary resetKeys={[loc.pathname]} regionLabel="This page">
                    {children}
                  </ErrorBoundary>
                </main>
              </div>
            </div>
            {mobileChatProject && !onBugDetail && (
              <nav className="chat-mobile-tabs" data-testid="chat-mobile-tabs" aria-label="Switch view">
                <button
                  type="button"
                  className={`chat-mobile-tabs__btn ${mobileChatView === 'chat' ? 'chat-mobile-tabs__btn--active' : ''}`}
                  aria-pressed={mobileChatView === 'chat'}
                  onClick={() => setMobileChatView('chat')}
                  data-testid="chat-mobile-tab-chat"
                  data-analytics-event="chat-mobile-tab-chat"
                >
                  <span className="chat-mobile-tabs__icon-wrap">
                    <MessageSquare size={18} strokeWidth={1.75} aria-hidden />
                    {chatHasUnseen && (
                      <span
                        className="chat-mobile-tabs__badge"
                        role="status"
                        aria-label="New chat message"
                        data-testid="chat-mobile-tab-badge"
                      />
                    )}
                  </span>
                  Chat
                </button>
                <button
                  type="button"
                  className={`chat-mobile-tabs__btn ${mobileChatView === 'project' ? 'chat-mobile-tabs__btn--active' : ''}`}
                  aria-pressed={mobileChatView === 'project'}
                  onClick={() => setMobileChatView('project')}
                  data-testid="chat-mobile-tab-project"
                  data-analytics-event="chat-mobile-tab-project"
                >
                  <LayoutGrid size={18} strokeWidth={1.75} aria-hidden />
                  Project
                </button>
              </nav>
            )}
            {/* Full-screen mobile takeover for Share / Settings (builder's account-page pattern):
                covers the chat/project view and the bottom tabs, with a back header to return. */}
            {mobileChatProject && mobileProjectPanel && chatProjectForModal && (
              <div className="mobile-panel" role="dialog" aria-modal="true" data-testid={`mobile-panel-${mobileProjectPanel}`}>
                <header className="mobile-panel__head">
                  <button
                    type="button"
                    className="chat-topbar__back"
                    onClick={() => setMobileProjectPanel(null)}
                    aria-label="Back"
                    data-testid="mobile-panel-back"
                    data-analytics-event="chat-topbar-back"
                  >
                    <ArrowLeft size={18} strokeWidth={1.75} aria-hidden />
                  </button>
                  <span className="chat-topbar__sep" aria-hidden />
                  <h2 className="mobile-panel__title">{mobileProjectPanel === 'share' ? 'Share' : 'Settings'}</h2>
                </header>
                <div className="mobile-panel__body">
                  {mobileProjectPanel === 'share' ? (
                    <ShareDialog
                      project={chatProjectForModal}
                      readOnly={!canEditProject}
                      inline
                      onClose={() => setMobileProjectPanel(null)}
                    />
                  ) : (
                    <div className="mobile-panel__settings">
                      <ProjectSettings project={chatProjectForModal} onClose={() => setMobileProjectPanel(null)} />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Desktop: Share / Settings open as modals over the project page (rail removed). */}
            {!mobileChatProject && projectModal === 'share' && chatProjectForModal && (
              <ShareDialog
                project={chatProjectForModal}
                readOnly={!canEditProject}
                onClose={() => setProjectModal(null)}
              />
            )}
            {!mobileChatProject && projectModal === 'settings' && chatProjectForModal && (
              <ModalPortal>
                {/* Same shell as the account settings modal (SettingsModal.css): sidebar of sections,
                    one section's rows on the right. ProjectSettings owns the dialog and its sidebar. */}
                <div
                  className="user-settings-modal__overlay"
                  data-testid="project-settings-modal"
                  onClick={() => setProjectModal(null)}
                  data-analytics-event="project-settings-overlay-close"
                >
                  <ProjectSettings variant="modal" project={chatProjectForModal} onClose={() => setProjectModal(null)} />
                </div>
              </ModalPortal>
            )}
          </div>
        ) : (
        <aside
          className={`app-sidebar ${effectiveCollapsed ? 'app-sidebar--collapsed' : ''}`}
          aria-label="Primary navigation"
          onClick={effectiveCollapsed ? handleCollapsedRailClick : undefined}
        >
          <div className="app-sidebar__top">
            {!effectiveCollapsed ? (
              <div className="app-sidebar__brand-row">
                <div className="app-sidebar__brand">
                  <img src="/images/logo.svg" alt="" width="22" height="26" />
                  <span className="app-sidebar__brand-text">Replay QA</span>
                </div>
                <button
                  type="button"
                  className="app-sidebar__pin-btn"
                  onClick={toggleCollapsed}
                  aria-label="Collapse sidebar"
                  data-analytics-event="nav-sidebar-collapse"
                >
                  <PanelLeft size={18} strokeWidth={1.75} aria-hidden />
                </button>
              </div>
            ) : (
              <div className="app-sidebar__brand-row app-sidebar__brand-row--collapsed">
                <div className="app-sidebar__tooltip-host">
                  <div className="app-sidebar__brand-hover app-sidebar__brand-hover--rail">
                    <div className="app-sidebar__brand-mark app-sidebar__brand-mark--collapsed" aria-hidden="true">
                      <img src="/images/logo.svg" alt="" width="22" height="26" />
                    </div>
                    <button
                      type="button"
                      className="app-sidebar__pin-btn app-sidebar__pin-btn--overlay"
                      onClick={toggleCollapsed}
                      aria-label="Expand sidebar"
                      data-analytics-event="nav-sidebar-expand"
                    >
                      <PanelRight size={18} strokeWidth={1.75} aria-hidden />
                    </button>
                  </div>
                  <span className="app-sidebar__tooltip" role="tooltip">
                    Expand sidebar
                  </span>
                </div>
              </div>
            )}
          </div>
          <>
          <nav className="app-sidebar__nav">
            {/* Both branches conditionally insert the flagged Insights link, so neither may paint
                before the experimental-flags state is known (`insightsLoaded`) — a late-resolving
                flag inserted the link after first paint and pushed the Projects section down 42px
                (bug-mu8zaw1l-zk8z). The footer is bottom-pinned via margin-top: auto, so the
                empty-while-loading nav moves nothing already on screen. */}
            {!insightsLoaded ? null : !effectiveCollapsed ? (
              // Desktop, no project open: the nut.new home sidebar — primary nav grouped above a
              // labelled "Projects" section that holds the searchable list.
              <>
                <Link
                  to="/projects"
                  className={`app-sidebar__link ${projectsNavActive ? 'app-sidebar__link--active' : ''}`}
                  aria-current={loc.pathname === '/projects' ? 'page' : undefined}
                  data-analytics-event="nav-projects-link"
                >
                  <LayoutGrid size={18} strokeWidth={1.75} aria-hidden />
                  Projects
                </Link>
                {insightsEnabled && (
                  <Link
                    to="/insights"
                    className={`app-sidebar__link ${loc.pathname === '/insights' ? 'app-sidebar__link--active' : ''}`}
                    aria-current={loc.pathname === '/insights' ? 'page' : undefined}
                    data-analytics-event="nav-insights-link"
                  >
                    <BarChart3 size={18} strokeWidth={1.75} aria-hidden />
                    Insights
                  </Link>
                )}
                <Link
                  to="/"
                  className={`app-sidebar__link ${dashboardActive ? 'app-sidebar__link--active' : ''}`}
                  aria-current={dashboardActive ? 'page' : undefined}
                  data-analytics-event="nav-new-project-link"
                >
                  <Plus size={18} strokeWidth={1.75} aria-hidden />
                  New project
                </Link>
                {/* Desktop only: on mobile this nav is a single horizontal strip of links, with no room
                    for a section label or a searchable list — the Projects link above goes to the full
                    page instead. The mobile media query also hides .sidebar-projects, which covers the
                    first frame (isMobileLayout is only known after the mount effect); this gate is what
                    keeps the label out of the strip and stops the list's 10s processing poll. */}
                {!isMobileLayout && (
                  <>
                    <div className="app-sidebar__section-label" aria-hidden>Projects</div>
                    <SidebarProjectList projectSearchInputRef={projectSearchInputRef} />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="app-sidebar__tooltip-host">
                  <Link
                    to="/projects"
                    className={`app-sidebar__link app-sidebar__link--icon-only ${projectsNavActive ? 'app-sidebar__link--active' : ''}`}
                    aria-current={loc.pathname === '/projects' ? 'page' : undefined}
                    aria-label="Projects"
                    data-analytics-event="nav-projects-link"
                  >
                    <LayoutGrid size={18} strokeWidth={1.75} aria-hidden />
                  </Link>
                  <span className="app-sidebar__tooltip" role="tooltip">
                    Projects
                  </span>
                </div>
                {insightsEnabled && (
                  <div className="app-sidebar__tooltip-host">
                    <Link
                      to="/insights"
                      className={`app-sidebar__link app-sidebar__link--icon-only ${loc.pathname === '/insights' ? 'app-sidebar__link--active' : ''}`}
                      aria-current={loc.pathname === '/insights' ? 'page' : undefined}
                      aria-label="Insights"
                      data-analytics-event="nav-insights-link"
                    >
                      <BarChart3 size={18} strokeWidth={1.75} aria-hidden />
                    </Link>
                    <span className="app-sidebar__tooltip" role="tooltip">
                      Insights
                    </span>
                  </div>
                )}
                <div className="app-sidebar__tooltip-host">
                  <Link
                    to="/"
                    className={`app-sidebar__link app-sidebar__link--icon-only ${dashboardActive ? 'app-sidebar__link--active' : ''}`}
                    aria-current={dashboardActive ? 'page' : undefined}
                    aria-label="New project"
                    data-analytics-event="nav-new-project-link"
                  >
                    <Plus size={18} strokeWidth={1.75} aria-hidden />
                  </Link>
                  <span className="app-sidebar__tooltip" role="tooltip">
                    New project
                  </span>
                </div>
              </>
            )}
          </nav>
          <div className="app-sidebar__footer">{sidebarFooterItems}</div>
          </>
        </aside>
        )}
        <HelpWidget
          appName="Replay QA"
          apiBase="/api"
          position="bottom-left"
          getAppState={getAppState}
          onNavigate={navigate}
          onEvent={handleHelpEvent}
          renderHooks={{
            renderButton: ({ isOpen, onClick }) => {
              helpToggleRef.current = onClick
              helpOpenRef.current = isOpen
              return null
            },
            renderHeader: (props) => <ChatHeader {...props} />,
            renderMessage: ({ message }) => <HelpMessageBubble message={message} />,
            // General help stays docked bottom-left.
            renderPanel: ({ children: panelChildren }) => createPortal(
              <div className="app-help-dialog help-widget" role="dialog" aria-label="Replay QA help" data-testid="help-dialog">
                <div className="app-help-dialog__body">{panelChildren}</div>
              </div>,
              document.body,
            ),
          }}
        />
        {!chatFullLayout && (
          <main className="app-main" id="main-content">
            <ErrorBoundary resetKeys={[loc.pathname]} regionLabel="This page">
              {children}
            </ErrorBoundary>
          </main>
        )}
      </div>
      <ChunkErrorBoundary eventTag="settings-modal" fallback={null}>
        <Suspense fallback={null}>
          <SettingsModal
            open={settingsOpen}
            initialTab={settingsTab}
            onClose={closeSettings}
            onTabChange={changeSettingsTab}
          />
        </Suspense>
      </ChunkErrorBoundary>
      {chatHelpOpen && (
        <ChatHelpOverlay
          onClose={() => setChatHelpOpen(false)}
          // The example questions become clickable only when a project chat is mounted to receive
          // them (the desktop or mobile full-page chat layout). Clicking one sends it into the chat;
          // on mobile, also flip to the chat pane so the reply is visible.
          onSelectExample={chatFullLayout ? (text) => {
            setChatHelpOpen(false)
            if (mobileChatProject) setMobileChatView('chat')
            requestChatPrompt(text)
          } : undefined}
        />
      )}
    </div>
    </HelpWidgetContext.Provider>
  )
}
